import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import { FiCalendar, FiList, FiFilter, FiDownload } from 'react-icons/fi';
import documentosLegalesService from '../services/documentosLegalesService';
import Loading from '../components/Loading';
import './FlujoCajaPage.css';

const FlujoCajaPage = () => {
  const [vistaActual, setVistaActual] = useState('calendario'); // 'calendario' o 'lista'
  const [eventos, setEventos] = useState([]);
  const [datosResumen, setDatosResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Filtros
  const hoy = new Date();
  const enUnMes = new Date();
  enUnMes.setMonth(enUnMes.getMonth() + 1);

  const [fechaDesde, setFechaDesde] = useState(hoy.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(enUnMes.toISOString().split('T')[0]);
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');

  useEffect(() => {
    cargarDatos();
  }, [vistaActual]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const response = await documentosLegalesService.getFlujoCaja(
        fechaDesde || null,
        fechaHasta,
        tipoDocumento || null,
        estadoFiltro || null,
        vistaActual
      );

      if (vistaActual === 'calendario') {
        setEventos(response.data.eventos || []);
      } else {
        setDatosResumen(response.data);
      }
    } catch (error) {
      console.error('Error al cargar flujo de caja:', error);
      toast.error(error.response?.data?.error || 'Error al cargar el flujo de caja');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltrar = () => {
    cargarDatos();
  };

  const handleEventClick = (clickInfo) => {
    const props = clickInfo.event.extendedProps;
    setDocumentoSeleccionado({
      id: props.documento_id,
      numero_documento: props.numero_documento,
      tipo_documento_label: props.tipo_documento_label,
      estado: props.estado,
      estado_label: props.estado_label,
      monto_total: props.monto_total,
      tercero: props.tercero,
      proyecto: props.proyecto,
      descripcion: props.descripcion,
      fecha_emision: props.fecha_emision,
      fecha_vencimiento: props.fecha_vencimiento,
      fecha_pago_programada: props.fecha_pago_programada,
    });
    setShowModal(true);
  };

  const handleMarcarPagado = async () => {
    if (!documentoSeleccionado) return;

    try {
      await documentosLegalesService.cambiarEstado(documentoSeleccionado.id, 'pagada');
      toast.success('Documento marcado como pagado');
      setShowModal(false);
      cargarDatos(); // Recargar datos
    } catch (error) {
      console.error('Error al marcar como pagado:', error);
      toast.error('Error al cambiar el estado del documento');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleExportarExcel = async () => {
    if (!datosResumen || vistaActual !== 'lista') {
      toast.warning('Cambia a vista de lista para exportar');
      return;
    }

    try {
      const XLSX = await import('xlsx');

      // Preparar datos
      const datos = [];
      datosResumen.datos.forEach(dia => {
        dia.documentos.forEach(doc => {
          datos.push({
            'Fecha Pago Programada': doc.fecha_pago_programada || doc.fecha_vencimiento,
            'Fecha Vencimiento': doc.fecha_vencimiento,
            'Numero': doc.numero_documento,
            'Tipo': doc.tipo_documento_label,
            'Tercero': doc.tercero,
            'Proyecto': doc.proyecto || '-',
            'Estado': doc.estado_label,
            'Monto': doc.monto_total,
            'Por Pagar': doc.es_por_pagar ? doc.monto_total : 0,
            'Por Cobrar': !doc.es_por_pagar ? doc.monto_total : 0
          });
        });
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datos);

      XLSX.utils.book_append_sheet(wb, ws, 'Flujo de Caja');
      XLSX.writeFile(wb, `flujo-caja-${fechaDesde}-${fechaHasta}.xlsx`);

      toast.success('Flujo de caja exportado a Excel');
    } catch (error) {
      console.error('Error al exportar:', error);
      toast.error('Error al exportar a Excel');
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="flujo-caja-page">
      {/* Header */}
      <div className="flujo-header">
        <div className="flujo-title-section">

        </div>

        <div className="vista-toggle">
          <button
            className={`btn-toggle ${vistaActual === 'calendario' ? 'active' : ''}`}
            onClick={() => setVistaActual('calendario')}
          >
            <FiCalendar /> Calendario
          </button>
          <button
            className={`btn-toggle ${vistaActual === 'lista' ? 'active' : ''}`}
            onClick={() => setVistaActual('lista')}
          >
            <FiList /> Lista
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-section">
        <div className="filtro-group">
          <label><FiCalendar /> Desde:</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="filtro-input"
          />
        </div>

        <div className="filtro-group">
          <label><FiCalendar /> Hasta:</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="filtro-input"
          />
        </div>

        <div className="filtro-group">
          <label><FiFilter /> Tipo:</label>
          <select
            value={tipoDocumento}
            onChange={(e) => setTipoDocumento(e.target.value)}
            className="filtro-select"
          >
            <option value="">Todos</option>
            <option value="FACTURA_COMPRA">Factura Compra</option>
            <option value="FACTURA_VENTA">Factura Venta</option>
            <option value="BOLETA_COMPRA">Boleta Compra</option>
            <option value="BOLETA_VENTA">Boleta Venta</option>
          </select>
        </div>

        <div className="filtro-group">
          <label><FiFilter /> Estado:</label>
          <select
            value={estadoFiltro}
            onChange={(e) => setEstadoFiltro(e.target.value)}
            className="filtro-select"
          >
            <option value="">Todos</option>
            <option value="emitida">Emitida</option>
            <option value="pagada">Pagada</option>
            <option value="vencida">Vencida</option>
          </select>
        </div>

        <button onClick={handleFiltrar} className="btn-filtrar">
          <FiFilter /> Filtrar
        </button>

        {vistaActual === 'lista' && (
          <button onClick={handleExportarExcel} className="btn-exportar">
            <FiDownload /> Exportar
          </button>
        )}
      </div>

      {/* Resumen KPIs (solo en vista lista) */}
      {vistaActual === 'lista' && datosResumen && (
        <div className="resumen-cards">
          <div className="resumen-card">
            <div className="resumen-label">Total Documentos</div>
            <div className="resumen-value">{datosResumen.resumen.total_documentos}</div>
          </div>
          <div className="resumen-card pagar">
            <div className="resumen-label">Por Pagar</div>
            <div className="resumen-value">{formatCurrency(datosResumen.resumen.total_por_pagar)}</div>
          </div>
          <div className="resumen-card cobrar">
            <div className="resumen-label">Por Cobrar</div>
            <div className="resumen-value">{formatCurrency(datosResumen.resumen.total_por_cobrar)}</div>
          </div>
          <div className={`resumen-card ${datosResumen.resumen.flujo_neto >= 0 ? 'positivo' : 'negativo'}`}>
            <div className="resumen-label">Flujo Neto</div>
            <div className="resumen-value">{formatCurrency(datosResumen.resumen.flujo_neto)}</div>
          </div>
          <div className="resumen-card vencidos">
            <div className="resumen-label">Vencidos</div>
            <div className="resumen-value">{datosResumen.resumen.documentos_vencidos}</div>
            <div className="resumen-sub">{formatCurrency(datosResumen.resumen.total_vencido)}</div>
          </div>
        </div>
      )}

      {/* Vista Calendario */}
      {vistaActual === 'calendario' && (
        <div className="calendario-container">
          <div className="leyenda">
            <div className="leyenda-item">
              <span className="leyenda-color" style={{ background: '#fd7e14' }}></span>
              <span>Por Pagar</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-color" style={{ background: '#0d6efd' }}></span>
              <span>Por Cobrar</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-color" style={{ background: '#198754' }}></span>
              <span>Pagado</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-color" style={{ background: '#dc3545' }}></span>
              <span>Vencido</span>
            </div>
          </div>

          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale={esLocale}
            events={eventos}
            eventClick={handleEventClick}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,dayGridWeek'
            }}
            height="auto"
            eventContent={(eventInfo) => (
              <div className="fc-event-content-custom">
                <div className="fc-event-title">{eventInfo.event.title}</div>
                <div className="fc-event-monto">{formatCurrency(eventInfo.event.extendedProps.monto_total)}</div>
              </div>
            )}
          />
        </div>
      )}

      {/* Vista Lista */}
      {vistaActual === 'lista' && datosResumen && (
        <div className="lista-container">
          {datosResumen.datos.map((dia, idx) => (
            <div key={idx} className="dia-grupo">
              <div className="dia-header">
                <h3>{formatDate(dia.fecha)}</h3>
                <div className="dia-totales">
                  <span className="total-pagar">Por Pagar: {formatCurrency(dia.total_pagar)}</span>
                  <span className="total-cobrar">Por Cobrar: {formatCurrency(dia.total_cobrar)}</span>
                  <span className={`flujo-neto ${dia.flujo_neto >= 0 ? 'positivo' : 'negativo'}`}>
                    Flujo: {formatCurrency(dia.flujo_neto)}
                  </span>
                </div>
              </div>

              <div className="documentos-lista">
                {dia.documentos.map((doc) => (
                  <div key={doc.id} className={`documento-card ${doc.es_por_pagar ? 'pagar' : 'cobrar'} ${doc.estado}`}>
                    <div className="documento-info">
                      <div className="documento-header-card">
                        <span className="documento-numero">{doc.numero_documento}</span>
                        <span className={`documento-tipo ${doc.tipo_documento}`}>{doc.tipo_documento_label}</span>
                        <span className={`documento-estado ${doc.estado}`}>{doc.estado_label}</span>
                      </div>
                      <div className="documento-tercero">{doc.tercero}</div>
                      {doc.proyecto && <div className="documento-proyecto">{doc.proyecto}</div>}
                      {doc.fecha_vencimiento && doc.fecha_pago_programada && doc.fecha_vencimiento !== doc.fecha_pago_programada && (
                        <div className="documento-descripcion" style={{ fontSize: '0.75rem', color: '#7c3aed' }}>
                          Venc. original: {formatDate(doc.fecha_vencimiento)}
                        </div>
                      )}
                      {doc.descripcion && <div className="documento-descripcion">{doc.descripcion}</div>}
                    </div>
                    <div className="documento-monto">
                      <div className="monto-label">{doc.es_por_pagar ? 'A Pagar' : 'A Cobrar'}</div>
                      <div className="monto-valor">{formatCurrency(doc.monto_total)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {datosResumen.datos.length === 0 && (
            <div className="no-data">No hay documentos en el período seleccionado</div>
          )}
        </div>
      )}

      {/* Modal de Detalle */}
      {showModal && documentoSeleccionado && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalle del Documento</h2>
              <button onClick={() => setShowModal(false)} className="btn-close">×</button>
            </div>

            <div className="modal-body">
              <div className="detalle-row">
                <span className="detalle-label">Número:</span>
                <span className="detalle-value">{documentoSeleccionado.numero_documento}</span>
              </div>
              <div className="detalle-row">
                <span className="detalle-label">Tipo:</span>
                <span className="detalle-value">{documentoSeleccionado.tipo_documento_label}</span>
              </div>
              <div className="detalle-row">
                <span className="detalle-label">Estado:</span>
                <span className={`estado-badge ${documentoSeleccionado.estado}`}>
                  {documentoSeleccionado.estado_label}
                </span>
              </div>
              <div className="detalle-row">
                <span className="detalle-label">Tercero:</span>
                <span className="detalle-value">{documentoSeleccionado.tercero}</span>
              </div>
              {documentoSeleccionado.proyecto && (
                <div className="detalle-row">
                  <span className="detalle-label">Proyecto:</span>
                  <span className="detalle-value">{documentoSeleccionado.proyecto}</span>
                </div>
              )}
              <div className="detalle-row">
                <span className="detalle-label">Fecha Emisión:</span>
                <span className="detalle-value">{formatDate(documentoSeleccionado.fecha_emision)}</span>
              </div>
              <div className="detalle-row">
                <span className="detalle-label">Fecha Vencimiento:</span>
                <span className="detalle-value">{formatDate(documentoSeleccionado.fecha_vencimiento)}</span>
              </div>
              {documentoSeleccionado.fecha_pago_programada && (
                <div className="detalle-row">
                  <span className="detalle-label">Fecha Pago Programada:</span>
                  <span className="detalle-value" style={{ color: '#7c3aed', fontWeight: 600 }}>
                    {formatDate(documentoSeleccionado.fecha_pago_programada)}
                  </span>
                </div>
              )}
              <div className="detalle-row">
                <span className="detalle-label">Monto:</span>
                <span className="detalle-value monto-grande">{formatCurrency(documentoSeleccionado.monto_total)}</span>
              </div>
              {documentoSeleccionado.descripcion && (
                <div className="detalle-row">
                  <span className="detalle-label">Descripción:</span>
                  <span className="detalle-value">{documentoSeleccionado.descripcion}</span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {documentoSeleccionado.estado !== 'pagada' && (
                <button onClick={handleMarcarPagado} className="btn-pagar">
                  Marcar como Pagado
                </button>
              )}
              <button onClick={() => setShowModal(false)} className="btn-cancelar">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlujoCajaPage;
